/* ==============================================================================
   Lista de membros do servidor
   ==============================================================================
   Painel da direita. Agrupa por cargo, mostra estado de voz, e é a porta de
   entrada para as ações de moderação — expulsar, silenciar, mover de sala,
   atribuir cargo.

   Três decisões que definem o componente
   --------------------------------------

   1. **O menu de contexto abre por botão direito E por teclado.** Botão direito
      é o gesto que o usuário pediu, mas se ele fosse o único caminho, quem
      navega por Tab não teria acesso a NENHUMA ação de moderação. Aqui abrem o
      menu: clique direito, a tecla de menu de contexto, `Shift+F10`, e o botão
      de reticências que aparece no hover/foco da linha. Os quatro chegam no
      mesmo lugar.

   2. **Ação sem permissão não aparece desabilitada — não aparece.** Um menu
      cheio de itens cinzas ensina o usuário a ignorar o menu. A exceção é
      quando o bloqueio é por HIERARQUIA e não por permissão: aí o item aparece
      com o motivo, porque "tenho a permissão mas não sobre esta pessoa" é uma
      informação que o moderador precisa.

   3. **O agrupamento é por cargo mais alto com `hoist`, e cargo sem `hoist`
      não cria grupo.** Sem isso, um servidor com 20 cargos decorativos produz
      20 grupos de um membro cada, e a lista deixa de comunicar hierarquia.

   O componente não decide autorização: recebe o bitfield já resolvido e os
   cargos de cada lado, e usa `canManageMember` de @concord/shared — a MESMA
   função que o servidor usa. O cálculo aqui é só para esconder UI; o servidor
   recalcula e é ele quem autoriza. Ver docs/ARCHITECTURE.md §11.
   ============================================================================== */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Permission,
  canManageMember,
  hasPermission,
  type RoleLike,
} from '@concord/shared';
import { Avatar, type AvatarMedia, type AvatarStatus } from '../components/Avatar.tsx';
import { VolumeSlider } from '../components/Controls.tsx';

/* ------------------------------------------------------------------------------
   Modelo
------------------------------------------------------------------------------ */

export interface MemberRole extends RoleLike {
  id: string;
  name: string;
  /** Cor do cargo em qualquer notação CSS. Ausente = herda o texto normal. */
  color?: string | null;
  /** Exibe o cargo como grupo separado na lista. */
  hoist: boolean;
}

export interface MemberVoiceState {
  channelId: string;
  channelName: string;
  selfMuted: boolean;
  selfDeafened: boolean;
  /** Silenciado por um moderador — diferente de ter desligado o próprio mic. */
  serverMuted: boolean;
  serverDeafened: boolean;
  speaking: boolean;
  /** Quantas telas este membro está compartilhando agora. */
  screenCount: number;
  cameraOn: boolean;
}

export interface MemberEntry {
  userId: string;
  /** Apelido na guild, quando houver; senão o nome de exibição. */
  displayName: string;
  username: string;
  nickname?: string | null;
  avatar?: AvatarMedia | null;
  status: AvatarStatus;
  /** Texto de status personalizado. */
  presenceText?: string | null;
  roles: MemberRole[];
  isOwner: boolean;
  isBot: boolean;

  /**
   * Relacao de amizade com quem esta olhando.
   *
   * `undefined` = desconhecida, e nesse caso nenhuma acao de amizade aparece.
   * E o default deliberado: um "adicionar amigo" para quem ja e amigo, ou para
   * quem ja mandou um pedido, produz um erro do servidor que a pessoa nao tinha
   * como prever.
   *
   *   none      da para enviar pedido
   *   outgoing  ja enviei, esperando resposta
   *   incoming  ele me enviou, da para aceitar
   *   friends   ja somos
   *   blocked   ha bloqueio entre os dois; nenhuma acao
   */
  friendship?: 'none' | 'outgoing' | 'incoming' | 'friends' | 'blocked' | undefined;
  voice?: MemberVoiceState | null;
}

/** Canal de voz para o submenu "mover para". */
export interface VoiceChannelTarget {
  id: string;
  name: string;
  userLimit: number | null;
  occupancy: number;
}

export interface MemberListProps {
  members: MemberEntry[];
  /** Todos os cargos da guild, ordenados por posição decrescente. */
  roles: MemberRole[];
  /** Quem está olhando. */
  viewer: {
    userId: string;
    roles: MemberRole[];
    isOwner: boolean;
    /** Bitfield já resolvido para esta guild. Serialize como string no JSON. */
    permissions: bigint;
  };
  voiceChannels: VoiceChannelTarget[];
  collapsed?: boolean;

  /**
   * Volume local por usuário, 0 a 1. Ausente = 1.
   *
   * Chaveado por `userId` aqui e por `sessionId` no palco, e a diferença é
   * proposital: a lista de membros fala de PESSOAS (uma linha por membro do
   * servidor), o palco fala de FONTES DE ÁUDIO (uma por dispositivo conectado).
   * Forçar a mesma chave nos dois obrigaria um dos dois a mentir.
   */
  volumes?: Record<string, number> | undefined;
  /**
   * Ajustar o volume de alguém SÓ PARA MIM.
   *
   * Ausente = o controle não aparece. Não é permissão — é o consumidor dizendo
   * que não tem onde aplicar o valor. Ver a nota em `VolumeSlider` sobre por que
   * isto não pode ser confundido com silenciar no servidor.
   */
  onSetVolume?: ((userId: string, volume: number) => void) | undefined;

  onOpenProfile: (userId: string) => void;
  onSendDirectMessage: (userId: string) => void;

  /**
   * Envia pedido de amizade. Ausente esconde a acao.
   *
   * A lista de membros e a de amigos sao coisas separadas de proposito --
   * amizade nao depende de compartilhar servidor. Mas o ENCONTRO acontece aqui:
   * a pessoa ve alguem num servidor e quer adicionar. Sem esta acao, o caminho
   * era decorar o nome de usuario e digita-lo na tela de amigos.
   */
  onAddFriend?: ((userId: string) => void) | undefined;

  /**
   * Aceita um pedido que ESTA pessoa enviou. Ausente esconde a acao.
   *
   * Separado de `onAddFriend` porque sao operacoes diferentes no servidor
   * (`POST /friends` contra `POST /friends/:id/accept`) e porque o rotulo tem
   * de dizer qual e: oferecer "enviar pedido" para quem ja mandou um seria
   * mentir sobre o que o clique faz -- o servidor trata como aceite.
   */
  onAcceptFriend?: ((userId: string) => void) | undefined;
  onManageRoles: (userId: string) => void;
  onToggleServerMute: (userId: string, mute: boolean) => void;
  onToggleServerDeafen: (userId: string, deafen: boolean) => void;
  onMoveToVoiceChannel: (userId: string, channelId: string) => void;
  onDisconnectFromVoice: (userId: string) => void;
  onKick: (userId: string) => void;
  onBan: (userId: string) => void;
}

/* ------------------------------------------------------------------------------
   Agrupamento
------------------------------------------------------------------------------ */

interface MemberGroup {
  key: string;
  label: string;
  color?: string | null;
  members: MemberEntry[];
}

/**
 * Agrupa por cargo hoisted de maior posição.
 *
 * "Offline" é um grupo separado e sempre o último — não porque offline importe
 * menos, mas porque misturar offline no meio de cada cargo faz a lista parecer
 * cheia de gente que não está lá. É a mesma razão pela qual a contagem no
 * cabeçalho conta só quem está online.
 */
function groupMembers(members: MemberEntry[], roles: MemberRole[]): MemberGroup[] {
  const hoisted = roles.filter((r) => r.hoist && !r.isEveryone);
  // Posição decrescente: cargo mais alto primeiro. `roles` já deveria vir
  // ordenado, mas ordenar aqui torna o componente independente disso.
  const ordered = [...hoisted].sort((a, b) => b.position - a.position);

  const buckets = new Map<string, MemberEntry[]>();
  for (const role of ordered) buckets.set(role.id, []);
  const noRole: MemberEntry[] = [];
  const offline: MemberEntry[] = [];

  for (const member of members) {
    if (member.status === 'offline') {
      offline.push(member);
      continue;
    }
    // Primeiro cargo hoisted que o membro possui, na ordem de posição.
    const top = ordered.find((role) => member.roles.some((r) => r.id === role.id));
    if (top) buckets.get(top.id)!.push(member);
    else noRole.push(member);
  }

  const byName = (a: MemberEntry, b: MemberEntry) =>
    a.displayName.localeCompare(b.displayName, 'pt-BR', { sensitivity: 'base' });

  const groups: MemberGroup[] = [];
  for (const role of ordered) {
    const bucket = buckets.get(role.id)!;
    if (bucket.length === 0) continue; // grupo vazio não vira cabeçalho
    groups.push({
      key: role.id,
      label: role.name,
      color: role.color ?? null,
      members: bucket.sort(byName),
    });
  }
  if (noRole.length > 0) {
    groups.push({ key: '@online', label: 'Online', members: noRole.sort(byName) });
  }
  if (offline.length > 0) {
    groups.push({ key: '@offline', label: 'Offline', members: offline.sort(byName) });
  }
  return groups;
}

/* ==============================================================================
   Componente
============================================================================== */

interface MenuState {
  member: MemberEntry;
  /** Coordenadas de viewport onde abrir. */
  x: number;
  y: number;
}

export function MemberList({
  members,
  roles,
  viewer,
  voiceChannels,
  collapsed = false,
  volumes,
  onSetVolume,
  onOpenProfile,
  onSendDirectMessage,
  onAddFriend,
  onAcceptFriend,
  onManageRoles,
  onToggleServerMute,
  onToggleServerDeafen,
  onMoveToVoiceChannel,
  onDisconnectFromVoice,
  onKick,
  onBan,
}: MemberListProps) {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (m) =>
        m.displayName.toLowerCase().includes(term) ||
        m.username.toLowerCase().includes(term),
    );
  }, [members, query]);

  const groups = useMemo(() => groupMembers(filtered, roles), [filtered, roles]);

  const onlineCount = useMemo(
    () => members.filter((m) => m.status !== 'offline').length,
    [members],
  );

  const openMenu = useCallback((member: MemberEntry, x: number, y: number) => {
    setMenu({ member, x, y });
  }, []);

  if (collapsed) return null;

  return (
    <div className="flex h-full flex-col">
      {/* ------------------------------------------------------------------------
          Cabeçalho e busca
          ------------------------------------------------------------------------
          A busca aparece a partir de 12 membros. Abaixo disso ela custa mais
          espaço vertical do que economiza em rolagem.
      ------------------------------------------------------------------------ */}
      <div className="shrink-0 px-3 pb-2 pt-3">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h2
            className="font-semibold uppercase"
            style={{
              fontSize: 'var(--text-2xs)',
              letterSpacing: 'var(--tracking-wide)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Membros
          </h2>
          <span
            style={{
              fontSize: 'var(--text-2xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {onlineCount}/{members.length}
          </span>
        </div>

        {members.length >= 12 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar membro"
            aria-label="Buscar membro"
            className="w-full px-2.5 outline-none"
            style={{
              height: 28,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-sunken)',
              boxShadow: '0 0 0 1px var(--color-border-subtle)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-primary)',
            }}
          />
        )}
      </div>

      {/* ------------------------------------------------------------------------
          Grupos
      ------------------------------------------------------------------------ */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {groups.length === 0 && (
          <p
            className="px-2 py-6 text-center"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
          >
            {query ? 'Nenhum membro corresponde à busca.' : 'Nenhum membro ainda.'}
          </p>
        )}

        {groups.map((group) => (
          <section key={group.key} className="mb-3">
            <h3
              className="px-2 pb-1 pt-1 font-semibold uppercase"
              style={{
                fontSize: 'var(--text-2xs)',
                letterSpacing: 'var(--tracking-wide)',
                /* Cabeçalho de grupo usa a cor do cargo, mas nunca a cor crua:
                   um cargo pode ser configurado num tom que reprova contraste
                   sobre a sidebar. A cor entra como matiz e a legibilidade fica
                   com o token. `color-mix` degrada para a cor do cargo em
                   navegador sem suporte, que é o pior caso aceitável. */
                color: group.color
                  ? `color-mix(in oklch, ${group.color} 70%, var(--color-text-secondary))`
                  : 'var(--color-text-tertiary)',
              }}
            >
              {group.label} — {group.members.length}
            </h3>

            <ul className="flex flex-col gap-px">
              {group.members.map((member) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  isSelf={member.userId === viewer.userId}
                  onOpen={() => onOpenProfile(member.userId)}
                  onRequestMenu={openMenu}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      {menu && (
        <MemberContextMenu
          state={menu}
          viewer={viewer}
          roles={roles}
          voiceChannels={voiceChannels}
          volume={volumes?.[menu.member.userId] ?? 1}
          {...(onSetVolume ? { onSetVolume } : {})}
          onClose={() => setMenu(null)}
          onOpenProfile={onOpenProfile}
          onSendDirectMessage={onSendDirectMessage}
          {...(onAddFriend ? { onAddFriend } : {})}
          {...(onAcceptFriend ? { onAcceptFriend } : {})}
          onManageRoles={onManageRoles}
          onToggleServerMute={onToggleServerMute}
          onToggleServerDeafen={onToggleServerDeafen}
          onMoveToVoiceChannel={onMoveToVoiceChannel}
          onDisconnectFromVoice={onDisconnectFromVoice}
          onKick={onKick}
          onBan={onBan}
        />
      )}
    </div>
  );
}

/* ==============================================================================
   Linha de membro
============================================================================== */

function MemberRow({
  member,
  isSelf,
  onOpen,
  onRequestMenu,
}: {
  member: MemberEntry;
  isSelf: boolean;
  onOpen: () => void;
  onRequestMenu: (member: MemberEntry, x: number, y: number) => void;
}) {
  const rowRef = useRef<HTMLLIElement>(null);
  const [hover, setHover] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  /** Ancora o menu no elemento quando a origem é o teclado — não há cursor. */
  const openFromElement = useCallback(() => {
    const rect = rowRef.current?.getBoundingClientRect();
    onRequestMenu(member, rect ? rect.right - 8 : 0, rect ? rect.bottom : 0);
  }, [member, onRequestMenu]);

  const offline = member.status === 'offline';
  const voice = member.voice;

  return (
    <li
      ref={rowRef}
      onContextMenu={(e) => {
        e.preventDefault();
        onRequestMenu(member, e.clientX, e.clientY);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocusWithin(false);
      }}
      className="group relative flex items-center gap-2 px-2 transition-colors"
      style={{
        minHeight: 36,
        borderRadius: 'var(--radius-md)',
        background: hover || focusWithin ? 'var(--color-surface-hover)' : 'transparent',
        /* O anel de status é recortado com a cor de fundo. Numa linha em hover a
           superfície muda, então o token do anel precisa acompanhar — senão fica
           um halo da cor antiga em volta da bolinha. */
        ['--color-avatar-ring' as string]:
          hover || focusWithin ? 'var(--color-surface-hover)' : 'var(--color-surface-raised)',
        /* Offline é ATENUADO, não escondido: 55% mantém o nome legível e
           comunica ausência sem exigir uma cor de texto separada. */
        opacity: offline ? 0.55 : 1,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        onKeyDown={(e) => {
          // Tecla de menu de contexto e Shift+F10: os dois caminhos de teclado
          // que o SO oferece para "menu deste item". Sem eles, moderar por
          // teclado seria impossível.
          if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            openFromElement();
          }
        }}
        className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none"
        style={{ borderRadius: 'var(--radius-md)' }}
      >
        <Avatar
          media={member.avatar ?? null}
          name={member.displayName}
          seed={member.userId}
          size={24}
          status={member.status}
          speaking={voice?.speaking ?? false}
          muted={Boolean(voice && (voice.selfMuted || voice.serverMuted))}
        />

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span
              className="truncate"
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                /* Cor do cargo mais alto que TENHA cor, não do cargo mais alto.
                   Um cargo administrativo sem cor não deve apagar a cor de um
                   cargo decorativo abaixo dele — é assim que o usuário espera. */
                color: memberColor(member) ?? 'var(--color-text-secondary)',
              }}
            >
              {member.displayName}
            </span>

            {member.isOwner && <OwnerCrown />}
            {member.isBot && <BotTag />}
          </span>

          {/* Segunda linha: onde a pessoa está, ou o que ela escreveu de status.
              Estado de voz ganha da mensagem de status — durante uma chamada,
              "onde ele está" é a informação acionável. */}
          {voice ? (
            <span
              className="flex items-center gap-1 truncate"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
            >
              <VoiceStateIcons voice={voice} />
              {voice.channelName}
            </span>
          ) : (
            member.presenceText && (
              <span
                className="block truncate"
                style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
              >
                {member.presenceText}
              </span>
            )
          )}
        </span>
      </button>

      {/* Reticências: o mesmo menu, para quem não usa botão direito e para
          toque, onde botão direito não existe. Só ocupa espaço quando visível. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRequestMenu(member, e.clientX, e.clientY);
        }}
        aria-label={`Ações para ${member.displayName}`}
        className="grid shrink-0 place-items-center transition-opacity"
        style={{
          width: 22,
          height: 22,
          borderRadius: 'var(--radius-sm)',
          opacity: hover || focusWithin ? 1 : 0,
          pointerEvents: hover || focusWithin ? 'auto' : 'none',
          color: 'var(--color-text-tertiary)',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="3.5" cy="8" r="1.3" />
          <circle cx="8" cy="8" r="1.3" />
          <circle cx="12.5" cy="8" r="1.3" />
        </svg>
      </button>

      {isSelf && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2"
          style={{
            width: 2,
            height: 16,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-accent)',
          }}
        />
      )}
    </li>
  );
}

/** Cor do cargo mais alto que tenha cor definida. */
function memberColor(member: MemberEntry): string | null {
  let best: MemberRole | null = null;
  for (const role of member.roles) {
    if (!role.color) continue;
    if (!best || role.position > best.position) best = role;
  }
  return best?.color ?? null;
}

function OwnerCrown() {
  return (
    <span title="Dono do servidor" className="shrink-0 leading-none">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M1.2 9.2L2 3.4l2 2 2-3.2 2 3.2 2-2 .8 5.8H1.2z"
              fill="var(--color-warning)" />
      </svg>
      <span className="sr-only">dono do servidor</span>
    </span>
  );
}

function BotTag() {
  return (
    <span
      className="shrink-0 font-bold uppercase"
      style={{
        fontSize: 9,
        padding: '1px 4px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-accent-subtle)',
        color: 'var(--color-accent-on-subtle)',
        letterSpacing: 'var(--tracking-wide)',
        boxShadow: '0 0 0 1px var(--color-accent-border)',
      }}
    >
      bot
    </span>
  );
}

/**
 * Ícones do estado de voz.
 *
 * Distingue silenciado por SI de silenciado pelo SERVIDOR — são situações
 * diferentes e a ação do moderador também: um ele não deve tocar, o outro é ele
 * quem aplicou. Sem essa distinção, o moderador tenta "dessilenciar" alguém que
 * apenas desligou o próprio microfone.
 */
function VoiceStateIcons({ voice }: { voice: MemberVoiceState }) {
  const items: Array<{ title: string; color: string; glyph: string }> = [];

  if (voice.serverMuted) {
    items.push({ title: 'silenciado pelo servidor', color: 'var(--color-voice-muted)', glyph: 'M' });
  } else if (voice.selfMuted) {
    items.push({ title: 'microfone desligado', color: 'var(--color-text-tertiary)', glyph: 'm' });
  }
  if (voice.serverDeafened || voice.selfDeafened) {
    items.push({
      title: voice.serverDeafened ? 'ensurdecido pelo servidor' : 'áudio desligado',
      color: voice.serverDeafened ? 'var(--color-voice-deafened)' : 'var(--color-text-tertiary)',
      glyph: 'd',
    });
  }
  if (voice.screenCount > 0) {
    items.push({
      title: `compartilhando ${voice.screenCount} tela${voice.screenCount > 1 ? 's' : ''}`,
      color: 'var(--color-accent-on-subtle)',
      glyph: String(voice.screenCount),
    });
  }
  if (voice.cameraOn) {
    items.push({ title: 'câmera ligada', color: 'var(--color-accent-on-subtle)', glyph: 'c' });
  }

  if (items.length === 0) return null;

  return (
    <span className="flex shrink-0 items-center gap-1">
      {items.map((item) => (
        <span
          key={item.title}
          title={item.title}
          className="grid place-items-center font-bold"
          style={{
            width: 12,
            height: 12,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-sunken)',
            color: item.color,
            fontSize: 8,
            lineHeight: 1,
          }}
        >
          {item.glyph}
          <span className="sr-only">{item.title}</span>
        </span>
      ))}
    </span>
  );
}

/* ==============================================================================
   Menu de contexto
============================================================================== */

interface MenuItemSpec {
  id: string;
  label: string;
  danger?: boolean;
  /** Presente = item visível mas inativo, com este motivo. */
  blockedReason?: string;
  onSelect?: () => void;
  /** Submenu de destinos (mover para canal de voz). */
  submenu?: Array<{ id: string; label: string; hint?: string; onSelect: () => void }>;
}

function MemberContextMenu({
  state,
  viewer,
  roles,
  voiceChannels,
  volume,
  onSetVolume,
  onClose,
  onOpenProfile,
  onSendDirectMessage,
  onAddFriend,
  onAcceptFriend,
  onManageRoles,
  onToggleServerMute,
  onToggleServerDeafen,
  onMoveToVoiceChannel,
  onDisconnectFromVoice,
  onKick,
  onBan,
}: {
  state: MenuState;
  viewer: MemberListProps['viewer'];
  roles: MemberRole[];
  voiceChannels: VoiceChannelTarget[];
  volume: number;
  onSetVolume?: ((userId: string, volume: number) => void) | undefined;
  onClose: () => void;
} & Pick<
  MemberListProps,
  | 'onOpenProfile'
  | 'onSendDirectMessage'
  | 'onAddFriend'
  | 'onAcceptFriend'
  | 'onManageRoles'
  | 'onToggleServerMute'
  | 'onToggleServerDeafen'
  | 'onMoveToVoiceChannel'
  | 'onDisconnectFromVoice'
  | 'onKick'
  | 'onBan'
>) {
  const { member } = state;
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: state.x, y: state.y });
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const isSelf = member.userId === viewer.userId;
  const perms = viewer.permissions;

  /*
   * Hierarquia e permissão são checagens SEPARADAS, e a diferença muda a UI.
   *
   * `canManageMember` é a mesma função do servidor — importar de @concord/shared
   * em vez de reimplementar é o que garante que o cliente não mostre uma ação
   * que o servidor vai recusar, nem esconda uma que ele aceitaria.
   */
  const outranks = canManageMember(viewer.roles, member.roles, {
    actorIsOwner: viewer.isOwner,
    targetIsOwner: member.isOwner,
  });

  const hierarchyBlock = isSelf
    ? undefined
    : outranks
      ? undefined
      : member.isOwner
        ? 'O dono do servidor não pode ser moderado.'
        : 'Este membro tem um cargo igual ou acima do seu.';

  const voice = member.voice;

  /* ---- Montagem dos itens ---- */
  const items: MenuItemSpec[] = [];

  items.push({
    id: 'profile',
    label: 'Ver perfil',
    onSelect: () => onOpenProfile(member.userId),
  });

  if (!isSelf && !member.isBot) {
    items.push({
      id: 'dm',
      label: 'Mensagem direta',
      onSelect: () => onSendDirectMessage(member.userId),
    });

    /* ---- Amizade ------------------------------------------------------------
       Fica junto de "mensagem direta" porque as duas sao acoes PESSOAIS: nao tem
       nada a ver com o servidor em que a pessoa foi encontrada. A moderacao vem
       depois, separada.

       Bot fica fora pelo `!member.isBot` acima: amizade com bot nao significa
       nada, ele nao aceita pedidos.
    ---------------------------------------------------------------------- */
    const rel = member.friendship;

    if (rel === 'incoming' && onAcceptFriend) {
      /*
       * Aceitar vem ANTES de qualquer outra opcao de amizade.
       *
       * Se a pessoa ja mandou um pedido, "enviar pedido" seria a acao errada
       * para oferecer: o servidor trataria como aceite, mas o rotulo teria
       * mentido sobre o que o clique ia fazer.
       */
      items.push({
        id: 'friend-accept',
        label: 'Aceitar convite de amizade',
        onSelect: () => onAcceptFriend(member.userId),
      });
    } else if (rel === 'none' && onAddFriend) {
      items.push({
        id: 'friend-add',
        label: 'Enviar convite de amizade',
        onSelect: () => onAddFriend(member.userId),
      });
    } else if (rel === 'outgoing') {
      /*
       * Convite pendente aparece BLOQUEADO, nao ausente.
       *
       * Esconder faria a pessoa procurar a acao, nao achar, e tentar de novo
       * digitando o nome na tela de amigos -- onde receberia um erro. Um item
       * cinza que diz "esperando resposta" responde a pergunta no lugar onde
       * ela foi feita.
       */
      items.push({
        id: 'friend-pending',
        label: 'Convite de amizade enviado',
        blockedReason: 'Esperando a resposta dele.',
      });
    }
    /*
     * `friends` e `blocked` nao geram item, e e intencional.
     *
     * Ja ser amigo nao pede acao aqui: remover amizade e no menu da lista de
     * amigos, que e onde se gerencia amizade. E bloqueio nao deve nem lembrar
     * que existe -- mostrar "desbloquear" no meio de um menu de servidor expoe
     * uma decisao pessoal num contexto coletivo.
     */
  }

  /* ---- Cargos ---- */
  if (hasPermission(perms, Permission.MANAGE_ROLES) && !isSelf) {
    // Só faz sentido oferecer se existir algum cargo atribuível: o ator precisa
    // estar ACIMA do cargo para atribuí-lo, e @everyone nunca é atribuível.
    const assignable = roles.filter(
      (r) => !r.isEveryone && (viewer.isOwner || r.position < highestPosition(viewer.roles)),
    );
    items.push({
      id: 'roles',
      label: 'Gerenciar cargos',
      ...(hierarchyBlock
        ? { blockedReason: hierarchyBlock }
        : assignable.length === 0
          ? { blockedReason: 'Nenhum cargo abaixo do seu para atribuir.' }
          : { onSelect: () => onManageRoles(member.userId) }),
    });
  }

  /* ---- Moderação de voz: só aparece se a pessoa ESTÁ em voz ---- */
  if (voice) {
    if (hasPermission(perms, Permission.MUTE_MEMBERS)) {
      items.push({
        id: 'mute',
        label: voice.serverMuted ? 'Reativar microfone' : 'Silenciar no servidor',
        ...(hierarchyBlock
          ? { blockedReason: hierarchyBlock }
          : { onSelect: () => onToggleServerMute(member.userId, !voice.serverMuted) }),
      });
    }

    if (hasPermission(perms, Permission.DEAFEN_MEMBERS)) {
      items.push({
        id: 'deafen',
        label: voice.serverDeafened ? 'Reativar áudio' : 'Ensurdecer no servidor',
        ...(hierarchyBlock
          ? { blockedReason: hierarchyBlock }
          : { onSelect: () => onToggleServerDeafen(member.userId, !voice.serverDeafened) }),
      });
    }

    if (hasPermission(perms, Permission.MOVE_MEMBERS)) {
      const targets = voiceChannels.filter((c) => c.id !== voice.channelId);
      items.push({
        id: 'move',
        label: 'Mover para',
        ...(hierarchyBlock
          ? { blockedReason: hierarchyBlock }
          : targets.length === 0
            ? { blockedReason: 'Não há outro canal de voz neste servidor.' }
            : {
                submenu: targets.map((channel) => {
                  // Canal cheio aparece, mas identificado: esconder faria o
                  // moderador procurar um canal que ele sabe existir.
                  const full =
                    channel.userLimit !== null && channel.occupancy >= channel.userLimit;
                  return {
                    id: channel.id,
                    label: channel.name,
                    hint: channel.userLimit
                      ? `${channel.occupancy}/${channel.userLimit}${full ? ' · cheio' : ''}`
                      : String(channel.occupancy),
                    onSelect: () => onMoveToVoiceChannel(member.userId, channel.id),
                  };
                }),
              }),
      });

      items.push({
        id: 'disconnect',
        label: 'Desconectar da voz',
        ...(hierarchyBlock
          ? { blockedReason: hierarchyBlock }
          : { onSelect: () => onDisconnectFromVoice(member.userId) }),
      });
    }
  }

  /* ---- Expulsar e banir: sempre por último e sempre marcados ---- */
  if (!isSelf && hasPermission(perms, Permission.KICK_MEMBERS)) {
    items.push({
      id: 'kick',
      label: 'Expulsar do servidor',
      danger: true,
      ...(hierarchyBlock ? { blockedReason: hierarchyBlock } : { onSelect: () => onKick(member.userId) }),
    });
  }
  if (!isSelf && hasPermission(perms, Permission.BAN_MEMBERS)) {
    items.push({
      id: 'ban',
      label: 'Banir do servidor',
      danger: true,
      ...(hierarchyBlock ? { blockedReason: hierarchyBlock } : { onSelect: () => onBan(member.userId) }),
    });
  }

  /*
   * Reposiciona para dentro da viewport DEPOIS de medir.
   *
   * Um menu aberto perto da borda direita ou inferior sairia da tela — e um
   * clique direito num membro do fim da lista cai exatamente ali. Medir e
   * corrigir num layout effect evita o quadro em que o menu aparece cortado.
   */
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    let { x, y } = state;
    if (x + rect.width > window.innerWidth - margin) x = window.innerWidth - rect.width - margin;
    if (y + rect.height > window.innerHeight - margin) y = y - rect.height;
    setPosition({ x: Math.max(margin, x), y: Math.max(margin, y) });
  }, [state]);

  /* Fecha ao clicar fora, ao rolar e no Escape. Rolagem entra na lista porque
     o menu é posicionado em coordenadas de viewport: rolar a lista deixaria o
     menu apontando para o membro errado. */
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  /* Move o foco para o menu ao abrir, para que Tab e as setas fiquem dentro
     dele — sem isso, abrir por teclado abriria um menu que o teclado não
     alcança. */
  useEffect(() => {
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus();
  }, []);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Ações para ${member.displayName}`}
      className="glass fixed py-1.5"
      style={{
        left: position.x,
        top: position.y,
        minWidth: 208,
        maxWidth: 264,
        borderRadius: 'var(--radius-lg)',
        /* Aresta de luz + sombra longa + brilho fraco, em vez de `--shadow-lg`
           puro. A escala de sombra é preta e calibrada para superfície sobre
           superfície; sobre o fundo escuro deste tema ela desaparece e o menu
           fica chapado contra a lista. Mesmo tratamento do `FloatingPanel`. */
        boxShadow:
          'var(--glass-edge), 0 18px 48px oklch(0% 0 0 / 0.55), var(--glow-soft)',
        zIndex: 'var(--z-overlay)',
      }}
    >
      {/* Cabeçalho: confirma sobre QUEM é o menu. Clique direito na lista
          errada é fácil, e uma ação destrutiva no membro errado é caro. */}
      <div className="flex items-center gap-2 px-3 pb-2 pt-1">
        <Avatar
          media={member.avatar ?? null}
          name={member.displayName}
          seed={member.userId}
          size={24}
        />
        <div className="min-w-0">
          <p
            className="truncate font-medium"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}
          >
            {member.displayName}
          </p>
          <p
            className="truncate"
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            @{member.username}
          </p>
        </div>
      </div>

      <Divider />

      {/* ----------------------------------------------------------------------
          Volume local — ANTES da lista de ações
          ----------------------------------------------------------------------
          Fica no topo e separado dos itens de moderação de propósito. É a única
          coisa neste menu que NÃO afeta ninguém além de quem clicou, e colocá-la
          entre "silenciar no servidor" e "expulsar" a faria parecer da mesma
          família — que é exatamente a confusão que custa caro aqui.

          Só aparece para quem está EM VOZ: um slider de volume para alguém que
          não está na chamada não tem o que controlar.
      ---------------------------------------------------------------------- */}
      {onSetVolume !== undefined && !isSelf && voice != null && (
        <>
          <VolumeSlider
            name={member.displayName}
            volume={volume}
            onChange={(next) => onSetVolume(member.userId, next)}
          />
          <Divider />
        </>
      )}

      {items.map((item, index) => {
        const prevDanger = index > 0 && items[index - 1]!.danger === true;
        return (
          <div key={item.id}>
            {item.danger && !prevDanger && <Divider />}
            <MenuRow
              item={item}
              submenuOpen={openSubmenu === item.id}
              onToggleSubmenu={() =>
                setOpenSubmenu((current) => (current === item.id ? null : item.id))
              }
              onDone={onClose}
            />
          </div>
        );
      })}
    </div>
  );
}

function highestPosition(roles: readonly MemberRole[]): number {
  return roles.reduce((max, r) => (r.position > max ? r.position : max), -1);
}

function Divider() {
  return (
    <span
      aria-hidden="true"
      className="my-1 block"
      style={{ height: 1, background: 'var(--color-border-subtle)' }}
    />
  );
}

function MenuRow({
  item,
  submenuOpen,
  onToggleSubmenu,
  onDone,
}: {
  item: MenuItemSpec;
  submenuOpen: boolean;
  onToggleSubmenu: () => void;
  onDone: () => void;
}) {
  const [hover, setHover] = useState(false);
  const blocked = Boolean(item.blockedReason);

  const color = blocked
    ? 'var(--color-text-disabled)'
    : item.danger
      ? 'var(--color-danger-on-subtle)'
      : 'var(--color-text-secondary)';

  /* Luz translúcida e não `--color-surface-hover`: dentro de um painel de vidro
     uma superfície opaca recorta um retângulo sólido no meio do material. */
  const background = hover && !blocked
    ? item.danger
      ? 'var(--color-danger-subtle)'
      : 'oklch(100% 0 0 / 0.07)'
    : 'transparent';

  return (
    <>
      <button
        type="button"
        role="menuitem"
        aria-disabled={blocked}
        aria-haspopup={item.submenu ? 'menu' : undefined}
        aria-expanded={item.submenu ? submenuOpen : undefined}
        title={item.blockedReason}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => {
          if (blocked) return;
          if (item.submenu) {
            onToggleSubmenu();
            return;
          }
          item.onSelect?.();
          onDone();
        }}
        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left outline-none"
        style={{ fontSize: 'var(--text-xs)', color, background, cursor: blocked ? 'default' : 'pointer' }}
      >
        <span className="truncate">{item.label}</span>
        {item.submenu && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
               style={{ transform: submenuOpen ? 'rotate(90deg)' : 'none', transition: 'transform var(--duration-fast)' }}>
            <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Motivo do bloqueio embaixo do item, não só no title: tooltip nativo
          não aparece por teclado nem em toque, e o motivo é a informação que
          impede o moderador de ficar clicando sem entender. */}
      {blocked && (
        <p
          className="px-3 pb-1.5"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          {item.blockedReason}
        </p>
      )}

      {/* Submenu embutido, não flutuante: um segundo popover posicionado ao lado
          sai da tela na borda direita, que é justamente onde este painel fica. */}
      {item.submenu && submenuOpen && (
        <ul role="menu" className="mb-1 ml-3 flex flex-col" style={{ boxShadow: 'inset 1px 0 0 oklch(100% 0 0 / 0.055)' }}>
          {item.submenu.map((entry) => (
            <li key={entry.id}>
              <SubmenuRow entry={entry} onDone={onDone} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function SubmenuRow({
  entry,
  onDone,
}: {
  entry: { id: string; label: string; hint?: string; onSelect: () => void };
  onDone: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        entry.onSelect();
        onDone();
      }}
      className="flex w-full items-center justify-between gap-2 py-1 pl-3 pr-3 text-left outline-none"
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-secondary)',
        background: hover ? 'oklch(100% 0 0 / 0.07)' : 'transparent',
      }}
    >
      <span className="truncate">{entry.label}</span>
      {entry.hint && (
        <span
          style={{
            fontSize: 'var(--text-2xs)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {entry.hint}
        </span>
      )}
    </button>
  );
}
